import React, { useRef, useState, useImperativeHandle, forwardRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useToaster } from "../../../hooks/useToaster";
import { SEVERITIES, SYSTEMSETTINGS } from "../../../data/constants";
import { formatFileSize, validateAttachments } from "../../../utils/messageHelper";
import { useSettingValue } from "../../../services/useSettingValue";
import { getFileExtension, renderSvgIcon } from "../../../utils/commonHelper";
import { Box, Paper, Stack, Tooltip, Typography, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
export type QuillWithDropRef = {
    getValue: () => string;
    getAttachments: () => File[];
    clear: () => void;
    addAttachments?: (files: File[]) => void;
    onChange?: (html: string) => void;
    getEditor: () => ReactQuill['editor'];
    focus: () => void;
    handleManualAttach: (files: File[]) => void;
};

type QuillWithDropProps = {
    onChange?: (html: string) => void;
};


const QuillWithDrop = forwardRef<QuillWithDropRef, QuillWithDropProps>(
    ({ onChange }, ref) => {

        const showToaster = useToaster();
        const [value, setValue] = useState("");
        const [attachments, setAttachments] = useState<File[]>([]);
        const [, setDragOver] = useState(false);
        const quillRef = useRef<ReactQuill>(null);
        const attachmentLimit = useSettingValue(SYSTEMSETTINGS.ATTACHMENT_LIMIT);
        const attachmentSize = useSettingValue(SYSTEMSETTINGS.ATTACHMENT_SIZE);
        const allowedExtensions = useSettingValue(SYSTEMSETTINGS.ATTACHMENT_EXTENSIONS);

        const validations = (files: File[]) => {
            const allowedExtArray = allowedExtensions
                ?.split(',')
                .map(ext => ext.trim().toLowerCase());
            const errorMessage = validateAttachments(files, attachments, attachmentSize, attachmentLimit, allowedExtArray);
            if (errorMessage) {
                showToaster(errorMessage, SEVERITIES.ERROR);
                return false;
            }
            return true;
        }

        useImperativeHandle(ref, () => ({
            getValue: () => value,
            getAttachments: () => attachments,
            clear: () => {
                setValue("");
                setAttachments([]);
            },
            addAttachments: (files: File[]) => {
                const newFiles = [...attachments, ...files];
                if (newFiles.length > 10) return;
                setAttachments(newFiles);
            },
            getEditor: () => quillRef.current?.getEditor(),
            focus: () => quillRef.current?.focus(),
            handleManualAttach: (files: File[]) => {
                if (validations(files)) {
                    setAttachments(prev => [...prev, ...files]);
                }
            },
        }));

        const handleEditorChange = (html: string) => {
            setValue(html);
            if (onChange) {
                onChange(html);
            }
        };

        const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragOver(false);

            const droppedFiles = Array.from(e.dataTransfer.files);

            if (validations(droppedFiles)) {
                const newFiles = [...attachments, ...droppedFiles];
                setAttachments(newFiles);
            }
        };

        const removeAttachment = (index: number) => {
            setAttachments((prev) => prev.filter((_, i) => i !== index));
        };

        const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragOver(true);
        };

        const handleDragLeave = () => setDragOver(false);

        return (
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                }}
            >
                <div>
                    <ReactQuill
                        bounds=".quill"
                        ref={quillRef}
                        value={value}
                        onChange={handleEditorChange}
                        theme="snow"
                        placeholder="Type your message..."
                        style={{ height: "200px" }}
                    />
                </div>
                <Box className="attachments-wrap">
                    {attachments.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {attachments.map((file, index) => (
                                <Paper
                                    key={index}
                                    elevation={1}
                                    sx={{
                                        borderRadius: 1,
                                        p: '8px 12px',
                                        mb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        maxWidth: '100%',
                                        bgcolor: '#f5f5f5',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ overflow: 'hidden', flex: 1 }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                fontSize: 18,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {renderSvgIcon(getFileExtension(file.name), 'file-icon')}
                                        </Box>

                                        <Tooltip title={file.name}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: '#333',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '700px',
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {file.name} ({formatFileSize(file.size || 0)})
                                            </Typography>
                                        </Tooltip>
                                    </Stack>


                                    <Tooltip title="Remove">
                                        <IconButton onClick={() => removeAttachment(index)} size="small">
                                            <CloseIcon fontSize="small" sx={{ color: '#999' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </Box>

            </div >
        );
    });

QuillWithDrop.displayName = "QuillWithDrop";

export default QuillWithDrop;
